import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const LanguageContext = createContext({})

export const useLanguage = () => {
  const context = useContext(LanguageContext)
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation()
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language)
  const [isChanging, setIsChanging] = useState(false)

  // Available languages
  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ]

  const changeLanguage = useCallback(async (languageCode) => {
    try {
      setIsChanging(true)

      // Change i18n language
      await i18n.changeLanguage(languageCode)

      // Update state
      setCurrentLanguage(languageCode)

      // Save to localStorage
      localStorage.setItem('language', languageCode)

      // Update document lang attribute
      document.documentElement.lang = languageCode
    } catch (error) {
      console.error('Failed to change language:', error)
    } finally {
      setIsChanging(false)
    }
  }, [i18n])

  useEffect(() => {
    // Get saved language from localStorage
    const savedLanguage = localStorage.getItem('language')
    if (savedLanguage && savedLanguage !== currentLanguage) {
      changeLanguage(savedLanguage)
    }
  }, [changeLanguage, currentLanguage])

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'zh' : 'en'
    changeLanguage(newLanguage)
  }

  const getCurrentLanguageData = () => {
    return languages.find(lang => lang.code === currentLanguage) || languages[0]
  }

  const value = {
    currentLanguage,
    languages,
    isChanging,
    changeLanguage,
    toggleLanguage,
    getCurrentLanguageData
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}
